<script setup lang="ts">
// B3.1 activation gate drawer. Mounted once by the shell-level mount
// component (see ActivationGateMount.vue); opens via the shell's
// activation-bus. Submits PATCH /api/v1/me through activationApi.
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import Drawer from 'primevue/drawer';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import { PhoneInput } from '@/shell';
import Message from 'primevue/message';
import RadioButton from 'primevue/radiobutton';
import { TermsSheet } from '@/features/registration';

import { NeonPayBankAccountField, NeonPayCardField } from '@neon-one/neon-pay-web';
import type { NeonPayTokenData } from '@neon-one/neon-pay-web';
import {
  activationGateOpen,
  isGatewayProvider,
  processorOf,
  railAvailable,
  resolveActivationGate,
  useAuctionEvent,
  useSession,
} from '@/shell';
import { useActivationStore } from '../store';

const DESKTOP_BREAKPOINT = 641; // --app-breakpoint-tablet from tokens.css
const drawerPosition = ref<'bottom' | 'right'>(
  typeof window !== 'undefined' && window.innerWidth >= DESKTOP_BREAKPOINT ? 'right' : 'bottom'
);

function onResize(): void {
  drawerPosition.value = window.innerWidth >= DESKTOP_BREAKPOINT ? 'right' : 'bottom';
}

onMounted(() => window.addEventListener('resize', onResize));
onUnmounted(() => window.removeEventListener('resize', onResize));

const cardFieldRef = ref<{ tokenize: () => Promise<void> } | null>(null);
const bankFieldRef = ref<{ tokenize: () => Promise<void> } | null>(null);
const store = useActivationStore();
const session = useSession();
const auctionEvent = useAuctionEvent();
const paymentProvider = computed(() => auctionEvent.event?.paymentProvider ?? null);
// GT1-411: distinct from paymentProvider — true whenever any general
// merchant exists (even a testMode one). paymentProvider alone can't tell
// "no processor anywhere" apart from "test mode, use the mock tokenizer".
const merchantConfigured = computed(() => auctionEvent.event?.merchantConfigured ?? false);

// The gate is opened through the shell bus (usePlaceBid, route guards), which
// cannot reach this store, so the draft is hydrated here on every opening.
// Without it the draft phone is empty and submit() sends `phone: null`,
// wiping the number on file and failing any SMS opt-in.
watch(
  activationGateOpen,
  (isOpen) => {
    if (isOpen) store.hydrateFromSession();
  },
  { immediate: true },
);

const open = computed({
  get: () => activationGateOpen.value,
  set: next => {
    if (!next) cancel();
  },
});

// GT1-488 — the activation gate must stay in lockstep with registration:
// Card and ACH only, never the wallets (they need a known price to tokenize
// and there is no amount at activation time either).
const achAvailable = computed(() =>
  railAvailable(
    { provider: paymentProvider.value, merchantConfigured: merchantConfigured.value },
    'ach',
  ),
);
const cardAvailable = computed(() =>
  railAvailable(
    { provider: paymentProvider.value, merchantConfigured: merchantConfigured.value },
    'card',
  ),
);
const processor = computed(() => processorOf(paymentProvider.value));
// Pay-Gateway merchants (`pk_` key) must construct the SDK with
// `{ gateway: true }`; without it the ACH field cannot tokenize at all.
const gateway = computed(() => isGatewayProvider(paymentProvider.value));

type PaymentRail = 'card' | 'ach';
const rail = ref<PaymentRail>('card');

watch(achAvailable, ok => {
  if (!ok && rail.value === 'ach') pickRail('card');
});

function pickRail(next: PaymentRail): void {
  if (rail.value === next) return;
  rail.value = next;
  store.setPaymentMethod('', null);
}

// No `phone` in the billing payload: AVS scores name, street and postal code,
// so phone buys nothing there — and it is the one field with format risk, where
// a number the processor will not parse becomes a fresh tokenization failure on
// the card rail. The gate still offers phone as an optional field; it just
// isn't sent to the tokenizer.
const billing = computed<NeonPayTokenData | undefined>(() => {
  const b = session.bidder;
  const first = store.draft.firstName.trim() || b?.firstName;
  const last = store.draft.lastName.trim() || b?.lastName;
  return {
    first_name: first || undefined,
    last_name: last || undefined,
    email: b?.email || undefined,
  };
});

// Offered, never demanded: phone gates nothing server-side, so it is shown as
// a convenience while the bidder is already here for payment or terms, and an
// empty one never blocks submit.
const showsPhone = computed(() => !session.bidder?.phone);

// GT1-565 — an admin who pre-registers a bidder may leave email opt-in off,
// and this drawer is the one screen that bidder is sure to see. Offered only
// while the flag is off, and unchecked by default: the bidder's own choice,
// never an override of what the admin recorded. An unchecked box sends
// nothing, so it cannot opt anyone out.
const organizationName = computed(() => auctionEvent.event?.organizationName ?? '');
const showsEmailOptIn = computed(() => session.bidder?.emailOptIn !== true);
// Same rule as registration: nothing to text without a number, whether it is
// already on file or being typed here.
const hasPhone = computed(() => !!(session.bidder?.phone || store.draft.phone.trim()));
const showsSmsOptIn = computed(() => session.bidder?.smsOptIn !== true && hasPhone.value);
const showsNotifications = computed(() => showsEmailOptIn.value || showsSmsOptIn.value);

watch(hasPhone, (present) => {
  if (!present && store.draft.smsOptIn) store.update('smsOptIn', false);
});
const showsPayment = computed(() => store.missingFields.includes('paymentMethod'));
const termsHtml = computed(() => auctionEvent.event?.termsAndConditionsHtml ?? '');
const hasTerms = computed(() => !!termsHtml.value.trim());
const showsTcAcceptance = computed(() => store.missingFields.includes('tcAcceptance') && hasTerms.value);
const termsSheetOpen = ref(false);

// No terms shown => tcAccepted stays false and is omitted from the PATCH.
// Never stamp an acceptance the bidder was never presented with: the server
// treats terms as satisfied when the auction resolves no T&C text.

function fieldError(name: string): string | null {
  const errs = store.fieldErrors[name];
  return errs && errs.length > 0 ? errs[0] : null;
}

const submitDisabled = computed(() => {
  if (store.state === 'submitting') return true;
  if (showsPayment.value && !paymentProvider.value && !store.draft.paymentMethodToken) return true;
  if (showsTcAcceptance.value && !store.draft.tcAccepted) return true;
  return false;
});

async function onSubmit(): Promise<void> {
  if (showsPayment.value && paymentProvider.value && !store.draft.paymentMethodToken) {
    // Real-provider mode mints the token here, on submit. The bank field's
    // tokenize() is safe on both processor paths (see NeonPayBankAccountField).
    if (rail.value === 'ach' && achAvailable.value) {
      await bankFieldRef.value?.tokenize();
    } else {
      await cardFieldRef.value?.tokenize();
    }
    if (!store.draft.paymentMethodToken) return;
  }
  const ok = await store.submit();
  if (ok) {
    resolveActivationGate(true);
    store.reset();
  }
}

function cancel(): void {
  resolveActivationGate(false);
  store.reset();
}
</script>

<template>
  <Drawer
    v-model:visible="open"
    :position="drawerPosition"
    :modal="true"
    :show-close-icon="true"
    header="Quick step before you bid"
    class="activation-drawer"
    data-testid="activation-gate"
  >
    <p
      class="activation__lead"
      data-testid="activation__lead"
    >
      We just need a couple more details. Then your bid is in.
    </p>

    <fieldset class="activation__fields">
      <div
        v-if="showsPhone"
        class="app-field-group"
      >
        <label
          for="activation-phone"
          class="app-field-label"
        >Phone <span class="activation__optional">(optional)</span></label>
        <PhoneInput
          id="activation-phone"
          :model-value="store.draft.phone"
          :invalid="fieldError('phone') !== null"
          data-testid="activation__phone"
          @update:model-value="store.update('phone', $event ?? '')"
        />
        <small
          v-if="fieldError('phone')"
          class="app-field-error"
        >{{ fieldError('phone') }}</small>
      </div>

      <div
        v-if="showsNotifications"
        class="app-field-group"
        data-testid="activation__notifications"
      >
        <span class="app-field-label">Notifications</span>
        <div
          v-if="showsEmailOptIn"
          class="app-checkbox-group"
        >
          <Checkbox
            v-model="store.draft.emailOptIn"
            input-id="activation-email-opt-in"
            binary
            data-testid="activation__email-opt-in"
          />
          <label
            for="activation-email-opt-in"
            class="activation__check-text"
          >Email me auction updates, like outbid alerts, item status, and closing reminders.</label>
        </div>
        <template v-if="showsSmsOptIn">
          <div class="app-checkbox-group">
            <Checkbox
              v-model="store.draft.smsOptIn"
              input-id="activation-sms-opt-in"
              binary
              :pt="{ input: { 'aria-describedby': 'activation-sms-disclosure' } }"
              data-testid="activation__sms-opt-in"
            />
            <label
              for="activation-sms-opt-in"
              class="activation__check-text"
            >Text me updates too: real-time outbid, item status, and closing alerts.</label>
          </div>
          <small
            id="activation-sms-disclosure"
            class="activation__helper"
            data-testid="activation__sms-disclosure"
          >
            We'll send a quick text to verify your number. Just reply START to turn on automated {{ organizationName }} auction updates. Message frequency varies based on your participation. Msg &amp; data rates may apply. Reply STOP to cancel anytime.
          </small>
        </template>
      </div>

      <div
        v-if="showsPayment && merchantConfigured"
        class="app-field-group"
        data-testid="activation__payment"
      >
        <p class="activation__payment-lead">
          {{ achAvailable ? 'Add a payment method to bid' : 'Add a card to bid' }}
        </p>
        <small class="app-field-helper activation__payment-helper">
          We don't charge anything now. We'll only charge if you win an item.
        </small>
        <div
          v-if="achAvailable && cardAvailable"
          class="activation__rails"
          role="radiogroup"
          aria-label="Payment method type"
        >
          <label
            class="activation__rail"
            :class="{ 'activation__rail--selected': rail === 'card' }"
            for="activation-rail-card"
          >
            <RadioButton
              v-model="rail"
              input-id="activation-rail-card"
              name="activation-rail"
              value="card"
              data-testid="activation__rail-card"
              @update:model-value="pickRail('card')"
            />
            <span>Card</span>
          </label>
          <label
            class="activation__rail"
            :class="{ 'activation__rail--selected': rail === 'ach' }"
            for="activation-rail-ach"
          >
            <RadioButton
              v-model="rail"
              input-id="activation-rail-ach"
              name="activation-rail"
              value="ach"
              data-testid="activation__rail-ach"
              @update:model-value="pickRail('ach')"
            />
            <span>Bank account</span>
          </label>
        </div>
        <!-- Keyed on the store's epoch so a card the issuer refused
             leaves a fresh, empty field to enter a different one on. Only the
             card rail carries a key: ACH is never authorized, so an ACH token
             can never come back declined. -->
        <NeonPayCardField
          v-if="rail === 'card' || !achAvailable"
          :key="store.paymentFieldEpoch"
          ref="cardFieldRef"
          :model-value="store.draft.paymentMethodToken"
          :provider="paymentProvider"
          :billing="billing"
          :invalid="fieldError('paymentMethodToken') !== null"
          :disabled="store.state === 'submitting'"
          size="large"
          @update:model-value="store.setPaymentMethod($event, 'card')"
        />
        <NeonPayBankAccountField
          v-else
          ref="bankFieldRef"
          :model-value="store.draft.paymentMethodToken"
          :provider="paymentProvider"
          :processor="processor"
          :gateway="gateway"
          :billing="billing"
          :invalid="fieldError('paymentMethodToken') !== null"
          :disabled="store.state === 'submitting'"
          size="large"
          data-testid="activation__payment-ach"
          @update:model-value="store.setPaymentMethod($event, 'ach')"
        />
        <small
          v-if="fieldError('paymentMethodToken')"
          class="app-field-error"
          role="alert"
        >{{
          fieldError('paymentMethodToken')
        }}</small>
      </div>
      <Message
        v-else-if="showsPayment && !merchantConfigured"
        severity="warn"
        :closable="false"
        data-testid="activation__payment-unavailable"
      >
        Payment isn't set up for this auction yet. Check back soon.
      </Message>

      <div
        v-if="showsTcAcceptance"
        class="app-checkbox-group"
      >
        <Checkbox
          v-model="store.draft.tcAccepted"
          input-id="activation-tc"
          binary
          :invalid="fieldError('tcAccepted') !== null"
          data-testid="activation__tc"
        />
        <label
          for="activation-tc"
          class="app-field-label"
        >I agree to the auction's
          <button
            type="button"
            class="activation__tc-link"
            @click.prevent="termsSheetOpen = true"
          >terms and conditions</button>
        </label>
      </div>

      <TermsSheet
        v-model:visible="termsSheetOpen"
        :html="termsHtml"
      />

      <Message
        v-if="store.formError"
        severity="error"
        :closable="false"
        data-testid="activation__form-error"
      >
        {{ store.formError }}
      </Message>
    </fieldset>

    <template #footer>
      <div class="activation__footer">
        <Button
          label="Cancel"
          severity="secondary"
          text
          data-testid="activation__cancel"
          @click="cancel"
        />
        <Button
          :label="store.state === 'submitting' ? 'Continuing…' : 'Continue'"
          size="large"
          icon="pi pi-arrow-right"
          icon-pos="right"
          :disabled="submitDisabled"
          :loading="store.state === 'submitting'"
          data-testid="activation__continue"
          @click="onSubmit"
        />
      </div>
    </template>
  </Drawer>
</template>

<style scoped>
.activation__optional {
  font-weight: var(--app-font-weight-regular);
  color: var(--app-text-muted);
}

/* Right (tablet+): fixed width wide enough for the tallest form combination */
:global(.p-drawer-right .p-drawer.activation-drawer) {
  width: 400px;
  max-width: 94vw;
}

/* Bottom (mobile): auto-height so it fits whatever fields are shown,
   capped at 85vh with rounded top corners matching the sheet pattern */
:global(.p-drawer-bottom .p-drawer.activation-drawer) {
  height: auto;
  max-height: 85vh;
  border-top-left-radius: var(--app-radius-xl);
  border-top-right-radius: var(--app-radius-xl);
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

/* Allow content to scroll when the combined form exceeds 85vh */
:global(.activation-drawer .p-drawer-content) {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

/* iOS safe-area clearance for the bottom drawer's action row */
:global(.p-drawer-bottom .activation-drawer .p-drawer-footer) {
  padding-bottom: calc(var(--app-space-12) + env(safe-area-inset-bottom));
}
.activation__check-text {
  font-size: var(--app-font-size-body-base);
  line-height: var(--app-line-height-body-base);
  color: var(--app-text-primary);
}
.activation__helper {
  display: block;
  font-size: var(--app-font-size-caption);
  line-height: var(--app-line-height-caption);
  color: var(--app-text-muted);
}
.activation__lead {
  margin: 0 0 var(--app-space-16);
  font-size: var(--app-font-size-body-base);
  line-height: var(--app-line-height-body-base);
  color: var(--app-text-muted);
}
.activation__fields {
  display: flex;
  flex-direction: column;
  gap: var(--app-space-16);
  border: none;
  padding: 0;
  margin: 0;
}
.activation__footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--app-space-12);
}
.activation__payment-lead {
  margin: 0 0 var(--app-space-4);
  font-size: var(--app-font-size-body-base);
  font-weight: var(--app-font-weight-semibold);
  color: var(--app-text-primary);
}
.activation__payment-helper {
  display: block;
  margin-bottom: var(--app-space-8);
}
.activation__rails {
  display: flex;
  gap: var(--app-space-8);
}
.activation__rail {
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: center;
  gap: var(--app-space-8);
  padding: var(--app-space-8) var(--app-space-12);
  border: var(--app-border-width-default) solid var(--app-border-muted);
  border-radius: var(--app-radius-md);
  cursor: pointer;
  font-size: var(--app-font-size-body-base);
  color: var(--app-text-primary);
}
.activation__rail--selected {
  border-color: var(--app-primary-color);
  background: var(--app-background-elevated);
}
.activation__tc-link {
  all: unset;
  cursor: pointer;
  color: var(--app-primary-color);
}
.activation__tc-link:hover {
  text-decoration: underline;
}
.activation__tc-link:focus-visible {
  outline: var(--app-border-width-default) solid var(--app-border-hover);
  outline-offset: 2px;
}
</style>
